import { ReactNode } from 'react';

import useFavicon from '@/hooks/useFavicon';

import { useDownloadOfferPdf } from '@/api/setters';
import { FormData, Stages } from '@/api/types';
import { isVehicleDamageProduct } from '@/components/helpers/productHelpers';
import Footer from '@/components/layouts/Footer';
import Navbar, { NavbarProps } from '@/components/layouts/Navbar';
import NavbarDeepDry from '@/components/layouts/NavbarDeepDry';
import SEO from '@/components/layouts/SEO';
import { CLAIM_PANEL, PRODUCT, WebsiteType } from '@/consts';
import { useAdditionalInfoContext } from '@/features/v2/lib/additionalInfo';
import { CaseCardVariants } from '@/features/v2/MobileMenu';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';

const Layout = ({
  children,
  withoutProgress = false,
  navbarProps,
  data,
  className,
  caseCard,
  mainClassName,
  variant = WebsiteType.HELPFIND,
}: {
  children: ReactNode;
  withoutProgress?: boolean;
  navbarProps?: Omit<NavbarProps, 'data'>;
  data?: FormData;
  className?: string;
  caseCard?: boolean;
  mainClassName?: string;
  variant?: WebsiteType;
}) => {
  const { i18n } = useLingui();
  const downloadPdfMutation = useDownloadOfferPdf({ enabled: !!data?.id });
  const { setError } = useAdditionalInfoContext();

  useFavicon(
    variant === WebsiteType.DEEPRY ? '/deepdry-favicon.png' : '/icon.png'
  );

  const id =
    data?.offer?.purchase_id ?? data?.offer?.opportunity_id ?? data?.id;
  const isOffer =
    (isVehicleDamageProduct(data?.product?.id) &&
      !!data?.offer?.opportunity_id) ||
    (data?.product?.id === PRODUCT.HOUSE_HOLD &&
      (data?.stage === Stages.OFFER || data?.stage === Stages.OFFER_FINISHED));

  const defaultTitle = id ? (
    <>
      <span className={'hidden text-dark-gray-100 xs:block'}>
        {data?.offer?.purchase_id
          ? i18n.t('Numer Twojej dopłaty:')
          : isOffer
            ? i18n.t('Numer Twojej propozycji:')
            : i18n.t('Numer Twojej weryfikacji:')}
      </span>
      <span>{id}</span>
    </>
  ) : null;

  const handleDownloadPdf = async () => {
    if (data) {
      await downloadPdfMutation.mutateAsync(data.id, {
        onSuccess: (data) => {
          const url = URL.createObjectURL(data);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'propozycja-helpfind.pdf';
          a.click();
        },
        onError: () => {
          setError(true);
        },
      });
    }
  };

  const isOfferPage =
    (isVehicleDamageProduct(data?.product?.id) &&
      data?.stage === Stages.STAGE_III) ||
    (data?.product?.id === PRODUCT.HOUSE_HOLD && data?.stage === Stages.OFFER);

  return (
    <div className={cn('m-0 flex min-h-[100dvh] flex-col p-0', className)}>
      <SEO id={data?.product_id || data?.product?.id || CLAIM_PANEL} />
      {variant === WebsiteType.DEEPRY ? (
        <NavbarDeepDry
          data={data}
          caseCardVariant={CaseCardVariants.EMPTY}
          phone="538 233 940"
          title={navbarProps?.title || defaultTitle}
        />
      ) : (
        <Navbar
          data={data}
          className={'pb-0'}
          title={navbarProps?.title || defaultTitle}
          caseCardVariant={
            navbarProps?.caseCardVariant ?? CaseCardVariants.EMPTY
          }
          downloadPdfClick={
            isOfferPage &&
            !data?.offer?.offer_rejected &&
            !data?.offer?.with_old_offer
              ? handleDownloadPdf
              : undefined
          }
          downloadPdfLoading={downloadPdfMutation.isPending}
          {...navbarProps}
        />
      )}
      <div
        className={cn(
          'flex w-full flex-col pb-[100px] xxs:pb-[125px] sm:pb-[110px] lg:hidden',
          withoutProgress && 'pb-[64px] xxs:pb-[64px] sm:pb-[64px]'
        )}
      />
      <main
        className={cn(
          'mx-auto w-full grow',
          'px-4',
          'py-12 pb-[86px] xxs:py-8 xxs:pb-[100px] lg:pb-[120px] lg:pt-[30px]',
          caseCard
            ? 'w-[670px] max-w-full md:px-0'
            : 'sm:max-w-screen-sm md:max-w-tablet md:px-6 lg:max-w-screen lg:px-8',
          withoutProgress &&
            'pb-[36px] pt-6 xxs:pb-[36px] xxs:pt-6 sm:pb-[36px] sm:pt-6 lg:py-10',
          mainClassName
        )}
      >
        <div className={'lg:space-y-5'}>{children}</div>
      </main>
      <Footer variant={variant} />
    </div>
  );
};

export default Layout;
